import { cn } from '@utils/cn';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { TFunction } from 'next-i18next';
import { useTranslation } from 'next-i18next';
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { ClickOutside } from '../../../components/ClickOutside';
import { Image } from '../../../components/Image';
import { ProductPrice } from '../../../components/ProductPrice/ProductPrice';
import type { CurrencyCode } from '../../../types/Locations';
import { Button } from '../Button';
import { CheckboxCard } from '../Forms/Checkbox/CheckboxCard';
import { Icon } from '../Icon';
import { Skeleton } from '../Skeleton/Skeleton';
import { type FrameTab, FrameTabs } from './FrameTabs';
import styles from './framing.module.css';
import type { FrameArtwork, FrameLayout, FrameOptionGroup } from './types';

export type FrameDrawerTab = 'selection' | 'guide';

export interface FrameDrawerProps {
  layout: FrameLayout;
  groups: FrameOptionGroup[];
  onSelect: (groupId: string, optionId: string) => void;
  /** Total framing cost of the current selection, from the shared pricing helpers. */
  total: number;
  currencyCode?: CurrencyCode;
  /**
   * Works that can be framed; the dropdown shows when there is more than one.
   * On `md+` (inline and modal) it sits above the options, on the Selection
   * tab only.
   */
  artworks?: FrameArtwork[];
  selectedArtworkId?: string;
  onArtworkChange?: (artworkId: string) => void;
  /**
   * The image for the current selection: the selected variant's Shopify image
   * (already framed), or the artwork when nothing is framed yet. Sits beside
   * the options on `md+` for inline and modal layouts, and above the tabs
   * otherwise.
   */
  previewImage?: { src: string; alt: string };
  /** Call-to-action slot below the options (e.g. Confirm / Continue unframed). */
  actions?: ReactNode;
  defaultTab?: FrameDrawerTab;
  isLoading?: boolean;
  /** Modal layout only: the fixed top bar's heading. Defaults to "Frame it your way". */
  title?: string;
  /** Modal layout only: shows the close button in the fixed top bar. */
  onClose?: () => void;
  className?: string;
}

const GUIDE_IMAGES = {
  float: '/images/framing/guide-float.png',
  window: '/images/framing/guide-window.png',
  standard: '/images/framing/guide-standard.png',
  museum: '/images/framing/guide-museum.png',
} as const;

const GUIDE_SECTIONS = [
  { key: 'mounting', items: ['float', 'window'] },
  { key: 'glazing', items: ['standard', 'museum'] },
] as const;

const getStringList = (t: TFunction, key: string): string[] => {
  const value = t(key, { returnObjects: true }) as unknown;
  return Array.isArray(value)
    ? (value as unknown[]).filter(
        (item): item is string => typeof item === 'string',
      )
    : [];
};

const ArtworkSelect = ({
  artworks,
  compact,
  onChange,
  selectedId,
}: {
  artworks: FrameArtwork[];
  compact: boolean;
  selectedId?: string;
  onChange?: (artworkId: string) => void;
}) => {
  const { t } = useTranslation('framing');
  const listId = useId();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);
  const selectedIndex = Math.max(
    artworks.findIndex((artwork) => artwork.id === selectedId),
    0,
  );
  const selected = artworks[selectedIndex];
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (open) optionRefs.current[selectedIndex]?.focus();
  }, [open, selectedIndex]);

  const focusOption = (index: number) => {
    const count = artworks.length;
    optionRefs.current[(index + count) % count]?.focus();
  };

  const choose = (artworkId: string) => {
    onChange?.(artworkId);
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onOptionKeyDown = (
    event: React.KeyboardEvent<HTMLLIElement>,
    index: number,
  ) => {
    const keyActions: Record<string, () => void> = {
      ArrowDown: () => focusOption(index + 1),
      ArrowUp: () => focusOption(index - 1),
      Home: () => focusOption(0),
      End: () => focusOption(artworks.length - 1),
      Enter: () => choose(artworks[index].id),
      ' ': () => choose(artworks[index].id),
      Escape: () => {
        setOpen(false);
        triggerRef.current?.focus();
      },
    };
    if (event.key === 'Tab') setOpen(false);
    const action = keyActions[event.key];
    if (!action) return;
    event.preventDefault();
    action();
  };

  const summary = (
    <>
      <span className={styles.UiArtworkSelect__thumb} data-compact={compact}>
        <Image
          alt={selected.thumbnail.alt}
          src={selected.thumbnail.src}
          width={64}
        />
      </span>
      <span className="flex min-w-0 grow flex-col text-left">
        <span className="truncate">{selected.artist ?? t('artwork')}</span>
        <span className={cn(styles.UiFrameDrawer__secondary, 'truncate')}>
          {selected.title}
        </span>
      </span>
    </>
  );

  return (
    <ClickOutside className="relative" onClickOutside={close}>
      <button
        ref={triggerRef}
        aria-controls={listId}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={styles.UiArtworkSelect__trigger}
        data-compact={compact}
        data-open={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          setOpen(true);
        }}
        type="button"
      >
        {summary}
        <Icon
          className="shrink-0"
          name={open ? 'ic_chevron_up' : 'ic_chevron_down'}
          size="sm"
        />
      </button>

      {open ? (
        <ul
          aria-label={t('artwork')}
          className={styles.UiArtworkSelect__list}
          data-compact={compact}
          id={listId}
          role="listbox"
        >
          {artworks.map((artwork, index) => {
            const isSelected = artwork.id === selected.id;

            return (
              <li
                key={artwork.id}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                aria-selected={isSelected}
                className={styles.UiArtworkSelect__option}
                onClick={() => choose(artwork.id)}
                onKeyDown={(event) => onOptionKeyDown(event, index)}
                role="option"
                tabIndex={-1}
              >
                <span
                  className={styles.UiArtworkSelect__optionThumb}
                  data-compact={compact}
                >
                  <Image
                    alt={artwork.thumbnail.alt}
                    src={artwork.thumbnail.src}
                    width={64}
                  />
                </span>
                <span className="line-clamp-2 min-w-0 grow">
                  {artwork.title}
                </span>
                {isSelected ? (
                  <Icon className="shrink-0" name="ic_check" size="sm" />
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </ClickOutside>
  );
};

/** Fades the old content out and the new in when `id` (the artwork) changes. */
const ArtworkFade = ({
  children,
  className,
  id,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
      <motion.div
        key={id ?? ''}
        animate={{ opacity: 1 }}
        className={className}
        exit={{ opacity: 0 }}
        initial={{ opacity: 0 }}
        transition={{ duration: shouldReduceMotion ? 0 : 0.3, ease: 'easeOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
};

const OptionGroups = ({
  compact,
  currencyCode,
  groups,
  isLoading,
  onSelect,
}: Pick<
  FrameDrawerProps,
  'currencyCode' | 'groups' | 'isLoading' | 'onSelect'
> & { compact: boolean }) => {
  const groupPrefix = useId();

  if (isLoading) {
    return (
      <div className="grid gap-3" data-testid="frame-content-loading">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className={styles.UiFrameDrawer__groups} data-compact={compact}>
      {groups
        .filter(({ options }) => options.length > 0)
        .map((group) => {
          const name = `${groupPrefix}-${group.id}`;

          return (
            <fieldset
              key={group.id}
              className={styles.UiFrameDrawer__group}
              data-compact={compact}
            >
              <legend className="mb-4 font-bold">{group.title}</legend>
              {group.options.map((option) => {
                const checked = option.id === group.selectedId;

                return (
                  <CheckboxCard
                    key={option.id}
                    checked={checked}
                    description={option.description}
                    features={option.features}
                    horizontal
                    id={`${name}-${option.id}`}
                    label={option.name}
                    name={name}
                    onChange={() => onSelect(group.id, option.id)}
                    suffix={
                      !checked && option.priceDelta !== 0 ? (
                        <ProductPrice
                          currencyCode={currencyCode}
                          prefix={option.priceDelta > 0 ? '+' : '-'}
                          price={Math.abs(option.priceDelta)}
                          skipConvertCurrency={!!currencyCode}
                          skipTaxCalculation
                        />
                      ) : undefined
                    }
                    tag={option.badge ?? undefined}
                    thumbnail={
                      option.thumbnail
                        ? {
                            filename: option.thumbnail.src,
                            alt: option.thumbnail.alt,
                          }
                        : undefined
                    }
                    type="radio"
                    value={option.id}
                  />
                );
              })}
            </fieldset>
          );
        })}
    </div>
  );
};

const FrameGuide = ({ compact }: { compact: boolean }) => {
  const { t } = useTranslation('framing');

  return (
    <div className={styles.UiFrameDrawer__groups} data-compact={compact}>
      {GUIDE_SECTIONS.map((section) => (
        <section key={section.key} className="grid gap-5">
          <h3 className="font-bold">{t(`guide.${section.key}.title`)}</h3>
          {section.items.map((item) => {
            const base = `guide.${section.key}.${item}`;
            const features = getStringList(t, `${base}.features`);

            return (
              <div
                key={item}
                className={cn(
                  styles.UiFrameDrawer__guideItem,
                  !compact && 'md:grid-cols-2',
                )}
              >
                <div className="overflow-hidden rounded-sm">
                  <Image
                    alt=""
                    size={{ sm: { ratio: '4/3', span: 12 } }}
                    src={GUIDE_IMAGES[item]}
                    width={384}
                  />
                </div>
                <div className="grid content-start gap-1">
                  <h4 className="font-bold">{t(`${base}.title`)}</h4>
                  {features.length ? (
                    <ul className="grid gap-1">
                      {features.map((feature) => (
                        <li key={feature} className="flex gap-1">
                          <Icon className="mt-1 shrink-0" name="ic_check" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>{t(`${base}.description`)}</p>
                  )}
                </div>
              </div>
            );
          })}
        </section>
      ))}
    </div>
  );
};

/** The nearest ancestor that scrolls `el`, else the page. */
const getScrollParent = (el: HTMLElement): HTMLElement => {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
  }
  return (document.scrollingElement ?? document.documentElement) as HTMLElement;
};

/**
 * Whether the sheet is a scrolling bottom sheet (mobile and drawer). On
 * desktop it is `display: contents`, which keeps its computed `overflow`.
 */
const isBottomSheet = (sheet: HTMLElement) => {
  const { display, overflowY } = getComputedStyle(sheet);
  return display !== 'contents' && overflowY === 'auto';
};

/**
 * What moves to raise the sheet: the drawer itself when it is its own
 * scroll box (inline on mobile, capped in height), else the nearest
 * scrolling ancestor, usually the page.
 */
const getSheetScroller = (root: HTMLElement): HTMLElement => {
  const { overflowY } = getComputedStyle(root);
  return overflowY === 'auto' || overflowY === 'scroll'
    ? root
    : getScrollParent(root);
};

/** The top of `scroller`'s scrollport on screen; sticky offsets start here. */
const getScrollportTop = (scroller: HTMLElement) =>
  scroller === document.scrollingElement ||
  scroller === document.documentElement
    ? 0
    : scroller.getBoundingClientRect().top + scroller.clientTop;

/**
 * Scrolls the drawer's scroller by `deltaY`, or the page once that has no
 * further to go. True when something moved.
 */
const scrollChain = (root: HTMLElement, deltaY: number) => {
  const scroller = getSheetScroller(root);
  const page = getScrollParent(root);
  for (const el of scroller === page ? [scroller] : [scroller, page]) {
    const before = el.scrollTop;
    el.scrollTop += deltaY;
    if (el.scrollTop !== before) return true;
  }
  return false;
};

/**
 * Mobile and drawer: stops the sheet halfway down the preview image. The
 * preview is sticky, so once it has stuck that line doesn't move; it's set
 * as `--UiFrameDrawer-sheet-top` and kept current as the layout resizes.
 */
const useSheetCap = (
  rootRef: RefObject<HTMLDivElement>,
  previewRef: RefObject<HTMLElement>,
) => {
  useEffect(() => {
    const root = rootRef.current;
    const preview = previewRef.current;
    const area = preview?.parentElement;
    if (!root || !preview || !area) return undefined;

    const update = () => {
      const areaRect = area.getBoundingClientRect();
      const previewRect = preview.getBoundingClientRect();
      // Where the image's middle sits once the preview area has stuck.
      const top =
        parseFloat(getComputedStyle(area).top) +
        (previewRect.top - areaRect.top) +
        previewRect.height / 2;
      if (Number.isFinite(top)) {
        root.style.setProperty('--UiFrameDrawer-sheet-top', `${top}px`);
      }
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(area);
    observer.observe(preview);
    return () => {
      observer.disconnect();
      root.style.removeProperty('--UiFrameDrawer-sheet-top');
    };
  }, [rootRef, previewRef]);
};

/**
 * Mobile and drawer, every layout alike: scrolling anywhere over the drawer
 * first raises the sheet to its cap (halfway down the preview image), and
 * only then scrolls its contents. Scrolling back up empties the sheet before
 * it drops again.
 */
const useSheetScroll = (
  rootRef: RefObject<HTMLDivElement>,
  sheetRef: RefObject<HTMLDivElement>,
) => {
  useEffect(() => {
    const root = rootRef.current;
    const sheet = sheetRef.current;
    if (!root || !sheet) return undefined;

    /**
     * Routes `deltaY` of scroll input. Down: the page first, until the sheet
     * has risen to its cap, then the sheet's contents. Up: the sheet's
     * contents back to the top, then (by returning false) the page. True when
     * it moved something, so the caller cancels the native scroll.
     */
    const route = (deltaY: number, inSheet: boolean) => {
      // On desktop the browser scrolls everything natively.
      if (!isBottomSheet(sheet)) return false;
      const style = getComputedStyle(sheet);
      const page = getSheetScroller(root);
      const toCap =
        sheet.getBoundingClientRect().top -
        getScrollportTop(page) -
        parseFloat(style.top);
      const rising = deltaY > 0 && toCap > 1;
      const sheetCanScroll =
        sheet.scrollTop + sheet.clientHeight < sheet.scrollHeight - 1;

      if (deltaY < 0) {
        if (sheet.scrollTop > 0) {
          // Over the sheet, native scrolling does this, more smoothly.
          if (inSheet) return false;
          sheet.scrollTop += deltaY;
          return true;
        }
        // The sheet is `overscroll-contain`, so over it the page has to be
        // moved by hand to let the sheet drop back down.
        if (!inSheet) return false;
        return scrollChain(root, deltaY);
      }

      // Over a sheet that has risen, native scrolling does the rest, until
      // it reaches its end: past that, the page carries on by hand, as the
      // sheet is `overscroll-contain`.
      if (inSheet && !rising) {
        return sheetCanScroll ? false : scrollChain(root, deltaY);
      }

      let rest = deltaY;
      if (rising) {
        const before = page.scrollTop;
        page.scrollTop += Math.min(rest, toCap);
        rest -= page.scrollTop - before;
      }
      if (rest > 0 && sheetCanScroll) sheet.scrollTop += rest;
      return rest < deltaY || (rest > 0 && sheetCanScroll);
    };

    const isInSheet = (target: EventTarget | null) =>
      target instanceof Node && sheet.contains(target);

    const onWheel = (event: WheelEvent) => {
      const deltaY =
        event.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? event.deltaY * 16
          : event.deltaY;
      if (route(deltaY, isInSheet(event.target))) event.preventDefault();
    };

    // A touch gesture is driven here from its first move if that move is
    // ours, since the browser won't hand a scroll it started back to us.
    let lastTouchY: number | null = null;
    let touchInSheet = false;
    let touchOwned: boolean | null = null;
    const onTouchStart = (event: TouchEvent) => {
      lastTouchY = event.touches[0].clientY;
      touchInSheet = isInSheet(event.target);
      touchOwned = null;
    };
    const onTouchMove = (event: TouchEvent) => {
      if (lastTouchY === null || touchOwned === false) return;
      const touchY = event.touches[0].clientY;
      const deltaY = lastTouchY - touchY;
      lastTouchY = touchY;
      // Once owned, the whole gesture is ours, including over the sheet.
      const handled = route(deltaY, touchOwned ? false : touchInSheet);
      if (touchOwned === null) touchOwned = handled;
      if (!touchOwned) return;
      if (event.cancelable) event.preventDefault();
      if (!handled) scrollChain(root, deltaY);
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    root.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
    };
  }, [rootRef, sheetRef]);
};

/**
 * The framing selection panel: Selection / Framing Guide tabs, the artwork
 * dropdown, data-driven option groups and a sticky total. Holds only UI state
 * (the active tab); selections and prices are owned by the caller.
 */
export const FrameDrawer = ({
  actions,
  artworks,
  className,
  currencyCode,
  defaultTab = 'selection',
  groups,
  isLoading = false,
  layout,
  onArtworkChange,
  onClose,
  onSelect,
  previewImage,
  selectedArtworkId,
  title,
  total,
}: FrameDrawerProps) => {
  const { t } = useTranslation('framing');
  const [tab, setTab] = useState<FrameDrawerTab>(defaultTab);
  const idPrefix = useId();
  const isCompact = layout === 'drawer';
  const rootRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLElement>(null);
  const shouldReduceMotion = useReducedMotion();
  useSheetCap(rootRef, previewRef);
  useSheetScroll(rootRef, sheetRef);

  /** Mobile and drawer: a tap on the peeking preview lowers a raised sheet
      back below the whole image. Only the page moves, so the sheet keeps its
      own scroll position, and scrolling raises it again from there. */
  const showFullPreview = () => {
    const sheet = sheetRef.current;
    const area = previewRef.current?.parentElement;
    const body = sheet?.parentElement;
    if (!rootRef.current || !sheet || !area || !body) return;
    if (!isBottomSheet(sheet)) return;
    const scroller = getSheetScroller(rootRef.current);
    const bodyStyle = getComputedStyle(body);
    const gap = parseFloat(bodyStyle.rowGap) || 0;
    // Where the sheet would be if it weren't sticky, and where it starts:
    // straight below the stuck preview area.
    const naturalTop =
      body.getBoundingClientRect().top -
      getScrollportTop(scroller) +
      parseFloat(bodyStyle.paddingTop) +
      area.offsetHeight +
      gap;
    const startTop =
      parseFloat(getComputedStyle(area).top) + area.offsetHeight + gap;
    const drop = startTop - naturalTop;
    if (drop <= 1) return;
    scroller.scrollBy({
      top: -drop,
      behavior: shouldReduceMotion ? 'auto' : 'smooth',
    });
  };

  /** Switching tabs starts the new tab's content from its top: anything
      scrolled up under the pinned tab bar is brought back down. */
  const changeTab = (next: FrameDrawerTab) => {
    const tabsEl = tabsRef.current;
    const panelEl = panelRef.current;
    const sheetEl = sheetRef.current;
    if (tabsEl && panelEl && sheetEl?.parentElement) {
      // On desktop the sheet is `display: contents`, so the tabs and panel
      // sit in the body's grid with its row gap between them.
      const gap =
        getComputedStyle(sheetEl).display === 'contents'
          ? parseFloat(getComputedStyle(sheetEl.parentElement).rowGap) || 0
          : 0;
      const offset =
        tabsEl.getBoundingClientRect().bottom +
        gap -
        panelEl.getBoundingClientRect().top;
      if (offset > 1) getScrollParent(panelEl).scrollTop -= offset;
      // Inline on desktop scrolls the options inside the panel itself.
      panelEl.scrollTop = 0;
    }
    setTab(next);
  };

  /** Mobile and drawer: a new choice changes the preview, so show all of it. */
  const selectOption = (groupId: string, optionId: string) => {
    onSelect(groupId, optionId);
    showFullPreview();
  };

  const tabs: Array<FrameTab<FrameDrawerTab>> = [
    { key: 'selection', label: t('tabs.selection') },
    {
      key: 'guide',
      label: (
        <>
          <Icon name="ic_info" />
          {t('tabs.guide')}
        </>
      ),
    },
  ];

  const preview = previewImage ? (
    <figure ref={previewRef} className={styles.UiFrameDrawer__preview}>
      {isLoading ? (
        <Skeleton className="absolute inset-0" />
      ) : (
        <ArtworkFade className="size-full" id={selectedArtworkId}>
          <img
            alt={previewImage.alt}
            className={styles.UiFrameDrawer__previewImage}
            src={previewImage.src}
          />
        </ArtworkFade>
      )}
      {/* Pointer shortcut only: scrolling does the same from the keyboard. */}
      <button
        aria-hidden
        className={styles.UiFrameDrawer__previewToggle}
        onClick={showFullPreview}
        tabIndex={-1}
        type="button"
      />
    </figure>
  ) : null;

  const artworkSelect =
    artworks && artworks.length > 1 ? (
      <ArtworkSelect
        artworks={artworks}
        compact={isCompact}
        onChange={onArtworkChange}
        selectedId={selectedArtworkId}
      />
    ) : null;

  return (
    <div
      ref={rootRef}
      className={cn(styles.UiFrameDrawer, className)}
      data-compact={isCompact}
      data-layout={layout}
    >
      {layout === 'modal' ? (
        <header className={styles.UiFrameDrawer__header}>
          <h2 className={styles.UiFrameDrawer__title}>{title ?? t('title')}</h2>
          {onClose ? (
            <span className={styles.UiFrameDrawer__close}>
              <Button
                hideLabel
                icon="ic_close"
                label={t('close')}
                onClick={onClose}
                size="xs"
                variant="transparent"
              />
            </span>
          ) : null}
        </header>
      ) : null}

      <div className={styles.UiFrameDrawer__body}>
        {artworkSelect || preview ? (
          <div className={styles.UiFrameDrawer__previewArea}>
            {artworkSelect ? (
              <div className={styles.UiFrameDrawer__artwork}>
                {artworkSelect}
              </div>
            ) : null}
            {preview}
          </div>
        ) : null}

        {/* A bottom sheet on mobile; on desktop its children join the grid. */}
        <div ref={sheetRef} className={styles.UiFrameDrawer__sheet}>
          <div ref={tabsRef} className={styles.UiFrameDrawer__tabs}>
            <FrameTabs<FrameDrawerTab>
              activeKey={tab}
              compact={isCompact}
              fullWidth
              idPrefix={idPrefix}
              onChange={changeTab}
              tabs={tabs}
            />
          </div>

          <div ref={panelRef} className={styles.UiFrameDrawer__panel}>
            {artworkSelect && tab === 'selection' ? (
              <div className={styles.UiFrameDrawer__artworkDesktop}>
                {artworkSelect}
              </div>
            ) : null}
            <div
              aria-labelledby={`${idPrefix}-tab-${tab}`}
              id={`${idPrefix}-panel-${tab}`}
              role="tabpanel"
            >
              {tab === 'selection' ? (
                <ArtworkFade id={selectedArtworkId}>
                  <OptionGroups
                    compact={isCompact}
                    currencyCode={currencyCode}
                    groups={groups}
                    isLoading={isLoading}
                    onSelect={selectOption}
                  />
                </ArtworkFade>
              ) : (
                <FrameGuide compact={isCompact} />
              )}
            </div>

            {actions && tab === 'selection' ? (
              <div className={styles.UiFrameDrawer__actions}>{actions}</div>
            ) : null}
          </div>
        </div>
      </div>

      <dl className={styles.UiFrameDrawer__footer}>
        <div className={styles.UiFrameDrawer__footerInner}>
          <dt>{t('total')}</dt>
          <dd>
            {isLoading ? (
              <Skeleton className="h-5 w-16" />
            ) : (
              <ArtworkFade id={selectedArtworkId}>
                <ProductPrice
                  currencyCode={currencyCode}
                  price={total}
                  skipConvertCurrency={!!currencyCode}
                  skipTaxCalculation
                />
              </ArtworkFade>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
};
